import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {Vector2d} from "konva/types/types";
import {RootState} from "../../editorStore";
import {distance, getEditorLine, lineAngle, midpoint} from "../../utils";
import {
	AddDropActionPayload,
	ChangeStyleActionPayload,
	DeleteArcPointActionPayload,
	DeleteManyActionPayload,
	EditorLine,
	EditorPoint,
	LengthCallback,
	LengthFragment,
	LengthFragmentCallback,
	LengthMeasurement,
	LengthMeasurementState,
	MaterialChangeActionPayload,
	MaterialMeasurementCreateActionPayload,
	UpdateChildItemNumericValueActionPayload,
	UpdateFragmentPointPositionActionPayload,
	UpdateFragmentPositionActionPayload,
	UpdateItemGenericValueActionPayload
} from "../../models/editor";
import {MeasurementType} from "../../models/enums";
import {
	ActionIdValuePayload,
	ActivateActionPayload,
	DeleteActionPayload,
	DeleteChildItemActionPayload,
	DeleteSelectedItemActionPayload,
	GroupedActionPayload,
	LineStyle,
	SetVisibilityActionPayload
} from "../../../../../base-konva/types";
import {getId} from "../../../../../../utils";

const initialState: LengthMeasurementState = {
	lengths: []
}

export const lengthSlice = createSlice({
	name: 'length',
	initialState: initialState,
	reducers: {
		reset: () => ({...initialState}),
		addLength: (state, {payload}: PayloadAction<MaterialMeasurementCreateActionPayload>) => {
			const lengthId = getId();
			const length: LengthMeasurement = {
				id: lengthId,
				material: payload.material,
				visible: true,
				style: payload.style as LineStyle,
				drops: [],
				type: MeasurementType.LENGTH,
				defaultDrop: payload.defaultDrop,
				lengthFragments: [],
				activeLengthFragmentId: null
			}
			state.lengths.push(length)
			state.activeLengthId = lengthId;
		},
		addPointToActiveFragment: (state, action: PayloadAction<Vector2d>) => {
			executeOnActiveFragment(state, (length, lengthFragment) => {
				if (lengthFragment.lastMouseUpPosition) {
					let lastActivePoint: EditorPoint = {
						id: getId(),
						position: lengthFragment.lastMouseUpPosition
					}
					if (lengthFragment.activePointId) {
						for (let line of lengthFragment.lines) {
							if (line.from.id === lengthFragment.activePointId) {
								lastActivePoint = line.from;
								break;
							}
							if (line.to.id === lengthFragment.activePointId) {
								lastActivePoint = line.to;
								break;
							}
						}
					}

					const newPoint: EditorPoint = {
						id: getId(),
						position: action.payload
					}
					lengthFragment.activePointId = newPoint.id
					lengthFragment.lastMouseUpPosition = newPoint.position
					lengthFragment.lines.push(getEditorLine(getId(), lastActivePoint, newPoint))
				}
				else if (lengthFragment.lines.length === 0) {
					lengthFragment.lastMouseUpPosition = action.payload
				}
			})
		},
		startNewLengthFragment: (state, action: PayloadAction<Vector2d>) => {
			executeOnActiveLength(state, (length => {
				const lengthFragmentId = getId();
				const lengthFragment: LengthFragment = {
					id: lengthFragmentId,
					lines: [],
					activePointId: null,
					lastMouseUpPosition: action.payload,
				}
				length.lengthFragments.push(lengthFragment)
				length.activeLengthFragmentId = lengthFragmentId;
			}))
		},
		updateLengthPointPosition: (state, action: PayloadAction<UpdateFragmentPointPositionActionPayload>) => {
			const {itemId: pointId, newPosition, fragmentId} = action.payload;

			executeOnSelectedFragment(state, fragmentId, (length, fragment) => {
				for (let line of fragment.lines) {
					let lineUpdate = false;

					if (line.from.id === pointId) {
						lineUpdate = true;
						line.from.position = newPosition;
					}
					else if (line.to.id === pointId) {
						lineUpdate = true;
						line.to.position = newPosition;
					}
					if (lineUpdate) {
						line.angle = lineAngle(line.from.position, line.to.position);
						line.distance = distance(line.from.position, line.to.position, line.arcPointPosition);
						line.center = midpoint(line.from.position, line.to.position)
					}
				}
			})
		},

		updateLengthArcPointPosition: (state, action: PayloadAction<UpdateFragmentPointPositionActionPayload>) => {
			const {itemId: lineId, newPosition, fragmentId} = action.payload;

			executeOnSelectedFragment(state, fragmentId, (length, fragment) => {
				for (let line of fragment.lines) {
					if (line.id === lineId) {
						line.arcPointPosition = newPosition
						line.distance = distance(line.from.position, line.to.position, newPosition);
					}
				}
			})
		},

		activateLengthPoint: (state, {payload}: PayloadAction<ActivateActionPayload>) => {
			const {id} = payload;

			executeOnActiveLength(state, length => {
				for (let lengthFragment of length.lengthFragments) {
					for (let line of lengthFragment.lines) {
						if (line.from.id === id) {
							length.activeLengthFragmentId = lengthFragment.id
							lengthFragment.activePointId = id
							lengthFragment.lastMouseUpPosition = line.from.position
							break;
						}
						if (line.to.id === id) {
							length.activeLengthFragmentId = lengthFragment.id
							lengthFragment.activePointId = id
							lengthFragment.lastMouseUpPosition = line.to.position
							break;
						}
					}
				}
			})
		},
		activateLength: (state, action: PayloadAction<ActivateActionPayload>) => {
			state.activeLengthId = action.payload.id;
			// Clear add-drop mode when switching to a different length
			if (state.addDropModeId && state.addDropModeId !== action.payload.id) {
				state.addDropModeId = undefined;
			}
		},
		clearActiveLength: (state, _action: PayloadAction<GroupedActionPayload>) => {
			state.activeLengthId = undefined;
			state.addDropModeId = undefined;
		},
		setWastage: (state, action: PayloadAction<ActivateActionPayload & { value: number | undefined }>) => {
			const {id, value} = action.payload
			executeOnSelectedLength(state, id, length => {
				length.wastage = value
			})
		},
		deleteLength: (state, {payload}: PayloadAction<DeleteActionPayload>) => {
			state.lengths = state.lengths.filter(l => l.id !== payload.id)
		},
		changeLengthStyle: (state, {payload}: PayloadAction<ChangeStyleActionPayload<LineStyle>>) => {
			const {id, style} = payload
			executeOnSelectedLength(state, id, length => {
				length.style = {...style}
			})
		},
		changeMaterial: (state, {payload}: PayloadAction<MaterialChangeActionPayload>) => {
			const {id, material, style} = payload;
			executeOnSelectedLength(state, id, length => {
				length.material = material
				length.style = style as LineStyle
			})
		},
		disableLinePreview: state => {
			executeOnActiveFragment(state, (length, fragment) => {
				length.activeLengthFragmentId = null;
				fragment.activePointId = null
				fragment.lastMouseUpPosition = null
			})
		},
		closeLoopOnActiveFragment: state => {
			executeOnActiveFragment(state, (length, fragment) => {
				// Must have at least 2 lines to form a loop (3+ points)
				if (fragment.lines.length >= 2 && fragment.activePointId) {
					// Get first point (from the first line)
					const firstPoint = fragment.lines[0].from;

					// Get last active point
					let lastActivePoint: EditorPoint | null = null;
					for (let line of fragment.lines) {
						if (line.from.id === fragment.activePointId) {
							lastActivePoint = line.from;
							break;
						}
						if (line.to.id === fragment.activePointId) {
							lastActivePoint = line.to;
							break;
						}
					}

					// Create closing line from last point to first point
					if (lastActivePoint) {
						fragment.lines.push(getEditorLine(getId(), lastActivePoint, firstPoint));
					}
				}

				// End drawing mode (same as disableLinePreview)
				length.activeLengthFragmentId = null;
				fragment.activePointId = null;
				fragment.lastMouseUpPosition = null;
			})
		},
		finishLines: state => {
			state.lengths = state.lengths.map(length => {
				length.activeLengthFragmentId = null
				length.lengthFragments = length.lengthFragments
					.filter(fragment => fragment.lines.length >= 1)
					.map(fragment => {
						fragment.activePointId = null
						fragment.lastMouseUpPosition = null
						return fragment
					})
				return length
			})
		},
		updateLengthFragmentPosition: (state, {payload}: PayloadAction<UpdateFragmentPositionActionPayload>) => {
			const {fragmentId, positionDelta: {x: xDelta, y: yDelta}} = payload;

			executeOnSelectedFragment(state, fragmentId, (length, fragment) => {
				fragment.lines = fragment.lines.map(line => {
					const from: EditorPoint = {
						...line.from,
						position: {
							x: line.from.position.x + xDelta,
							y: line.from.position.y + yDelta
						}
					}
					const to: EditorPoint = {
						...line.to,
						position: {
							x: line.to.position.x + xDelta,
							y: line.to.position.y + yDelta
						}
					}
					let arcPointPosition: Vector2d | undefined;
					if (line.arcPointPosition) {
						arcPointPosition = {
							x: line.arcPointPosition.x + xDelta,
							y: line.arcPointPosition.y + yDelta
						}
					}
					return getEditorLine(line.id, from, to, arcPointPosition)
				})
			})
		},
		setLengths: (state, {payload}: PayloadAction<ActionIdValuePayload<LengthMeasurement[]>>) => {
			state.lengths = payload.value
		},
		addDropToActive: (state, {payload}: PayloadAction<AddDropActionPayload>) => {
			executeOnActiveLength(state, length => {
				length.drops.push({id: getId(), ...payload})
			})
		},
		insertDropPointOnLine: (state, {payload}: PayloadAction<{fragmentId: string, lineId: string, position: Vector2d}>) => {
			executeOnActiveLength(state, length => {
				const fragment = length.lengthFragments.find(f => f.id === payload.fragmentId);
				if (!fragment) return;

				const lineIndex = fragment.lines.findIndex(l => l.id === payload.lineId);
				if (lineIndex === -1) return;

				const line = fragment.lines[lineIndex];

				// Don't split arc segments - would lose arc geometry
				if (line.arcPointPosition) return;

				// Create new point at the click position
				const newPoint: EditorPoint = {
					id: getId(),
					position: payload.position
				};

				// Create two new lines: from→newPoint and newPoint→to
				const line1 = getEditorLine(getId(), line.from, newPoint);
				const line2 = getEditorLine(getId(), newPoint, line.to);

				// Replace the original line with the two new lines
				fragment.lines.splice(lineIndex, 1, line1, line2);

				// Add a drop to the new point with defaultDrop value
				length.drops.push({
					id: getId(),
					pointId: newPoint.id,
					value: length.defaultDrop ?? 0
				});
			})
		},
		setDropValue: (state, {payload}: PayloadAction<UpdateChildItemNumericValueActionPayload>) => {
			const {id, parentId, value} = payload
			executeOnSelectedLength(state, parentId, length => {
				for (let drop of length.drops) {
					if (drop.id === id) {
						drop.value = value
						break;
					}
				}
			})
		},
		setDefaultDropValue: (state, {payload}: PayloadAction<UpdateItemGenericValueActionPayload<number | undefined>>) => {
			const {id, value} = payload
			executeOnSelectedLength(state, id, length => {
				length.defaultDrop = value
			})
		},
		setAllDropsValue: (state, {payload}: PayloadAction<UpdateItemGenericValueActionPayload<number | undefined>>) => {
			const {id, value} = payload
			executeOnSelectedLength(state, id, length => {
				for (let drop of length.drops) {
					drop.value = value ?? 0;
				}
			})
		},
		deleteDrop: (state, {payload}: PayloadAction<DeleteChildItemActionPayload>) => {
			const {id, parentId} = payload
			executeOnSelectedLength(state, parentId, length => {
				length.drops = length.drops.filter(item => item.id !== id)
			})
		},
		setVisibility: (state, {payload}: PayloadAction<SetVisibilityActionPayload>) => {
			const {id, visible, setAll} = payload;
			for (let measurement of state.lengths) {
				if (measurement.id === id || setAll) {
					measurement.visible = visible;
					if (!setAll) break;
				}
			}
		},
		removeItems: (state, {payload}: PayloadAction<DeleteManyActionPayload>) => {
			state.lengths = state.lengths.filter(l => !payload.ids.some(id => id === l.id));
		},
		removeSelectionItems: (state, {payload}: PayloadAction<DeleteSelectedItemActionPayload>) => {
			let lineSet: EditorLine[][] = [];
			for (let measurement of state.lengths) {
				if (measurement.id === payload.id) {
					for (let lengthFragment of measurement.lengthFragments) {
						if (lengthFragment.id === payload.fragmentId) {
							lengthFragment.lines.forEach((line: EditorLine) => {
								if (!payload.subIds!.some(lineToRemove => line.id === lineToRemove))
									if (lineSet.length === 0)
										lineSet.push([line]);
									else lineSet.some((set: EditorLine[], index) => {
										let hasConnection = set.some((setLine: EditorLine) => {
											return (setLine.to.position.x === line.to.position.x && setLine.to.position.y ===
												line.to.position.y) ||
												(setLine.from.position.x === line.to.position.x && setLine.from.position.y ===
													line.to.position.y) ||
												(setLine.to.position.x === line.from.position.x && setLine.to.position.y ===
													line.from.position.y) ||
												(setLine.from.position.x === line.from.position.x &&
													setLine.from.position.y ===
													line.from.position.y)
										});
										if (hasConnection) {
											lineSet[index].push(line);
										}
										else
											lineSet.push([line]);
										return true;
									});
							});
							if (lineSet.length === 0) {
								measurement.lengthFragments =
									measurement.lengthFragments.filter(item => item.id !== lengthFragment.id);
							}
							else {
								lengthFragment.lines = lineSet[0];
							}
						}
					}
				}
			}
			for (let measurement of state.lengths) {
				if (measurement.id === payload.id) {
					// Filter drops: keep if pointId exists in edited fragment's remaining lines OR in other fragments
					measurement.drops = measurement.drops.filter(item => {
						// Check if pointId exists in the remaining lines of the edited fragment
						const existsInEditedFragment = lineSet.some(
							set => set.some(line => line.from.id === item.pointId || line.to.id === item.pointId)
						);
						if (existsInEditedFragment) return true;

						// Check if pointId exists in OTHER fragments (not being edited)
						return measurement.lengthFragments
							.filter(frag => frag.id !== payload.fragmentId)
							.some(frag => frag.lines.some(line =>
								line.from.id === item.pointId || line.to.id === item.pointId
							));
					});
					lineSet.forEach((lines, index) => {
						if (index !== 0)
							measurement.lengthFragments.push(
								{id: getId(), lines: lines, activePointId: null, lastMouseUpPosition: null});

					});
				}
			}
		},
		removeArcPoint: (state, {payload}: PayloadAction<DeleteArcPointActionPayload>) => {
			const {fragmentId, subIds} = payload;
			if (fragmentId)
				executeOnSelectedFragment(state, fragmentId, (length, fragment) => {
					fragment.lines.forEach(line => {
						if (subIds?.includes(line.id)) {
							line.arcPointPosition = undefined;
							line.distance = distance(line.from.position, line.to.position);
						}
					})
				})
		},
		setAddDropMode: (state, {payload}: PayloadAction<{lengthId: string}>) => {
			state.addDropModeId = payload.lengthId;
		},
		clearAddDropMode: (state) => {
			state.addDropModeId = undefined;
		},
	}
})

function executeOnActiveFragment(state: LengthMeasurementState, callback: LengthFragmentCallback) {
	executeOnActiveLength(state, length => {
		for (let lengthFragment of length.lengthFragments) {
			if (lengthFragment.id === length.activeLengthFragmentId) {
				callback(length, lengthFragment);
				break;
			}
		}
	})
}

function executeOnSelectedFragment(state: LengthMeasurementState, fragmentId: string, callback: LengthFragmentCallback) {
	executeOnActiveLength(state, length => {
		for (let lengthFragment of length.lengthFragments) {
			if (lengthFragment.id === fragmentId) {
				callback(length, lengthFragment);
				break;
			}
		}
	})
}

function executeOnActiveLength(state: LengthMeasurementState, callback: LengthCallback) {
	for (let length of state.lengths) {
		if (length.id === state.activeLengthId) {
			callback(length);
			break;
		}
	}
}

function executeOnSelectedLength(state: LengthMeasurementState, lengthId: string, callback: LengthCallback) {
	for (let length of state.lengths) {
		if (length.id === lengthId) {
			callback(length);
			break;
		}
	}
}

export const lengthActions = lengthSlice.actions;
export const selectLengths = (state: RootState) => state.undoGroup.present.length.lengths;
export const selectActiveLengthId = (state: RootState) => state.undoGroup.present.length.activeLengthId;
export const selectAddDropModeId = (state: RootState) => state.undoGroup.present.length.addDropModeId;
export const lengthReducer = lengthSlice.reducer;
